import React, { Component } from 'react';
import Table, { ColumnProps } from 'antd/lib/table';
import umbrella from 'umbrella-storage';
import { Tooltip, Modal } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { getStates } from '../../components/public/StatesSelect';
import './page-customer.less';
import { KEY_CUSTOMER_CANCEL_CONFIRM_MESSAGE, KEY_CUSTOMER_CHANGE_CONFIRM_MESSAGE, KEY_CUSTOMER_HINT_MESSAGE } from '../../config/strcfg';
import CustomerService, { CustomerListRequest, ICustomerInfo, ICustomerInfoList } from '../../service/customer/CustomerService';

interface CustomerTableProps {}

interface CustomerTableState {
    pageData: ICustomerInfoList | null;
    sourceData: ICustomerInfo[];
    statesSelectValue: number | null;
    pagination: any;
}

//获取pageSize
const defaultPageSize = 5;
const getCustomerPageSize = () => {
    return umbrella.getLocalStorage('customerPageSize') ? umbrella.getLocalStorage('customerPageSize') : defaultPageSize;
}

export const mapGender = (value: number): string => {
    switch (value) {
        case 0:
            return '';
        case 1:
            return '男';
        case 2:
            return '女';
        default:
            return '';
    }
}

//自定义CustomerTable
class CustomerTable extends Component<CustomerTableProps, CustomerTableState> {

    state: CustomerTableState = {
        pageData: {
            records: [],
            total: 0,
            size: 0,
            current: 0,
            pages: 0
        },
        sourceData: [],
        statesSelectValue: null,
        pagination: null
    };

    renderTooltip = (text) => {
        return (
            <Tooltip title={text}>
                <span>{text}</span>
            </Tooltip>
        );
    };

    //定义表格的列属性
    columns: ColumnProps<any>[] = [
        {
            title: (
                <>
                    重点
                    <Tooltip title={KEY_CUSTOMER_HINT_MESSAGE}>
                        <QuestionCircleOutlined style={{ marginLeft: 4, cursor: 'pointer' }} />
                    </Tooltip>
                </>
            ),
            dataIndex: 'keyCustomer',
            key: 'keyCustomer',
            width: 80,
            render: (text, record: ICustomerInfo) => (
                <input
                    type="checkbox"
                    checked={record.keyCustomer}
                    onChange={() => this.handleCheckboxChange(record)}
                />
            ),
        },
        {
            title: '姓名',
            width: 100,
            dataIndex: 'cname',
            key: 'cname',
            render: (text: string, record: ICustomerInfo) => (
                <a href={`/#/app/customer/manager?id=${record.id}`}>{text}</a>
            ),
        },
        { title: '英文名', width: 105, dataIndex: 'ename', key: 'ename' },
        {
            title: '性别', width: 60, dataIndex: 'gender', key: 'gender',
            render: (text) => { return mapGender(text) },
        },
        { title: '状态', width: 100, dataIndex: 'states', key: 'states' },
        { title: '手机号', width: 130, dataIndex: 'mobile', key: 'mobile' },
        { title: '固话', width: 130, dataIndex: 'phone', key: 'phone' },
        { title: '职位', width: 150, dataIndex: 'position', key: 'position' },
        { title: '部门', width: 150, dataIndex: 'department', key: 'department' },
        { title: '邮箱', width: 280, dataIndex: 'email', key: 'email' },
        { title: '微信', width: 150, dataIndex: 'wechat', key: 'wechat' },
        { title: '传真', width: 150, dataIndex: 'fax', key: 'fax' },
        {
            title: '备注信息', width: 200, dataIndex: 'remark', key: 'remark',
            render: (text) => <span className="col-customer_remark"> {this.renderTooltip(text)} </span>,
        },
        {
            title: '操作',
            key: 'operation',
            fixed: 'right',
            width: 200,
            render: (record: ICustomerInfo) => (
                <>
                    <button onClick={() => this.handleContactBottonClick(record)}>沟通</button>
                    <button onClick={() => this.handleRequirementBottonClick(record)}>需求</button>
                    <button onClick={() => this.handleDeleteBottonClick(record)}>删除</button>
                </>
            ),
        },
    ];

    //重点联系人选择框事件
    handleCheckboxChange = (record: ICustomerInfo) => {
        // 移除当前焦点
        (document.activeElement as HTMLElement)?.blur();
    
        const newKeyCustomerValue = !record.keyCustomer;
        const confirmMessage = newKeyCustomerValue ? KEY_CUSTOMER_CHANGE_CONFIRM_MESSAGE : KEY_CUSTOMER_CANCEL_CONFIRM_MESSAGE;
        Modal.confirm({
            title: confirmMessage,
            onOk: () => {
                const updatedCustomer = {
                    id: record.id,
                    keyCustomer: newKeyCustomerValue,
                };
                CustomerService.updateCustomer(updatedCustomer)
                    .then(() => {
                        // 更新sourceData中的对应记录
                        this.setState((prevState) => {
                            const updatedSourceData = prevState.sourceData.map((item) => {
                                if (item.id === record.id) {
                                    return { ...item, keyCustomer: newKeyCustomerValue };
                                }
                                return item;
                            });
                            return { ...prevState, sourceData: updatedSourceData };
                        });
    
                        // 重新加载表格数据
                        const hash = window.location.hash;
                        const queryString = hash.includes('?') ? hash.split('?')[1] : '';
                        const urlParams = new URLSearchParams(queryString);
                        const companyId = parseInt(urlParams.get('company') || '-1', 10);
                        this.getCustomerTableData(this.state.pageData.current, companyId);
                    })
                    .catch(error => {
                        console.error('Error updating customer:', error);
                    });
            },
        });
    };

    //获取指定页码的查询数据
    getCustomerTableData = async (pageNo: number, company: number, states?: number) => {
        //设置state       
        if (states !== undefined) {
            this.state.statesSelectValue = states;
        }

        //构造请求
        const request: CustomerListRequest = {
            pageNo,
            pageSize: getCustomerPageSize(),
        };
        //处理返回
        const response = await CustomerService.getCustomerByCompanyId(company, request);
        if (response) {
            const pageData: ICustomerInfoList = response.data && response.data.customerInfoList ? response.data.customerInfoList : [];
            const customerRecords = pageData && pageData.records ? response.data.customerInfoList.records : [];
            const mappedSourceData: ICustomerInfo[] = customerRecords.map((item: any) => ({
                id: item.id,
                company: item.company,
                cname: item.cname,
                ename: item.ename,
                gender: item.gender,
                mobile: item.mobile,
                phone: item.phone,
                wechat: item.wechat,
                email: item.email,
                position: item.position,
                department: item.department,
                fax: item.fax,
                keyCustomer: item.keyCustomer,
                states: getStates(item.states),
                remark: item.remark,
                lastEditTime: item.lastEditTime,
            }));
            this.setState((prevState) => ({
                ...prevState,
                pageData: pageData,
                sourceData: mappedSourceData,
                pagination: {
                    ...prevState.pagination,
                    total: pageData.total,
                },
            }));
        }
    };

    //页码切换事件
    handleChangePage = async (pageNo: number) => {
        const hash = window.location.hash;
        const queryString = hash.includes('?') ? hash.split('?')[1] : '';
        const urlParams = new URLSearchParams(queryString);
        const companyId = parseInt(urlParams.get('company') || '-1', 10);
        await this.getCustomerTableData(pageNo, companyId);
        this.setState((prevState) => ({
            ...prevState,
            pageData: {
                ...prevState.pageData,
                current: pageNo,
            },
            pagination: { ...prevState.pagination, current: pageNo },
        }));
    };

    //PageSize选项卡事件
    changePageSize = (current: number, pageSize: number) => {
        umbrella.setLocalStorage('customerPageSize', pageSize);
        this.setState({
            pagination: { ...this.state.pagination, current: 1, pageSize },
        });
    };

    handleDeleteBottonClick = (customerData: ICustomerInfo) => {
        Modal.confirm({ 
            title: '确认删除', content: '是否确认删除该客户信息？', 
            onOk: () => CustomerService.deleteCustomer(customerData.id).then(() => {
                // 重新加载表格数据
                const hash = window.location.hash;
                const queryString = hash.includes('?') ? hash.split('?')[1] : '';
                const urlParams = new URLSearchParams(queryString);
                const companyId = parseInt(urlParams.get('company') || '-1', 10);
                this.getCustomerTableData(this.state.pageData.current, companyId);
            })
        });  
    }

    //客户沟通记录按钮事件
    handleContactBottonClick = (customerData: ICustomerInfo) => {
        // 构造查询参数，包含客户姓名和公司ID
        const queryParams = new URLSearchParams({
            customerName: customerData.cname,
            companyId: customerData.company.toString(),
            customer: customerData.id.toString()
        });
        // 跳转新增客户沟通记录页面
        window.location.href = `/#/app/customer/contact/manager?${queryParams.toString()}`; 
    }


    //客户沟通记录按钮事件
    handleRequirementBottonClick = (customerData: ICustomerInfo) => {
        // 构造查询参数，包含客户姓名和公司ID
        const queryParams = new URLSearchParams({
            customerName: customerData.cname,
            companyId: customerData.company.toString(),
            customer: customerData.id.toString()
        });
        // 跳转新增客户需求页面
        window.location.href = `/#/app/customer/requirement/manager?${queryParams.toString()}`;
    }
    

    render() {
        const sourceData = this.state.sourceData;
        const dataArray: ICustomerInfo[] = Array.isArray(sourceData) ? sourceData : [];
        const dataWithKeys = dataArray.map((row: ICustomerInfo, index: number) => ({ ...row, key: index }));

        return (
            <Table size="small"
                columns={this.columns}
                dataSource={dataWithKeys}
                pagination={{
                    showSizeChanger: true,
                    current: this.state.pageData ? this.state.pageData.current : 1,
                    pageSize: getCustomerPageSize(),
                    total: this.state.pageData ? this.state.pageData.total : 0,
                    pageSizeOptions: ['5', '10', '20', '50', '100'],
                    showTotal: (total: number, range: [number, number]) => `显示第 ${range[0]} 到第 ${range[1]} 条记录，共 ${total} 条记录`,
                    onShowSizeChange: (current, pageSize) => this.changePageSize(current, pageSize),
                    onChange: this.handleChangePage,
                }}
                scroll={{ x: 800, y: 230 }}
            />
        );
    }

};

export default CustomerTable;